import { useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faEllipsis, faSort, faTriangleExclamation, faArrowRight } from "@fortawesome/free-solid-svg-icons";

const toneClassMap = {
  success: "success",
  ok: "success",
  warning: "warning",
  danger: "danger",
  error: "danger",
  info: "info",
  neutral: "neutral",
};

const normalizeText = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const getToneClass = (value) => {
  const tone = typeof value === "object" && value !== null ? value.tone : value;
  const normalized = normalizeText(tone);

  if (toneClassMap[normalized]) return toneClassMap[normalized];
  if (["ok", "regle", "reglee", "valide", "actif", "success"].includes(normalized)) return "success";
  if (["a verifier", "verifier", "warning", "attention", "pending", "moyenne", "medium"].includes(normalized)) return "warning";
  if (["ko", "erreur", "critique", "critical", "haute", "high", "bloque", "danger"].includes(normalized)) return "danger";
  if (["info", "informationnelle", "informationnel", "faible", "low", "total"].includes(normalized)) return "info";

  return "neutral";
};

const getRawLabel = (value) => {
  if (typeof value === "object" && value !== null) {
    return value.label ?? value.value ?? "";
  }

  return value;
};

const isRightArrow = (value) => {
  const rawValue = String(getRawLabel(value) ?? "").trim();

  return (
    rawValue === "→" ||
    rawValue === "->" ||
    /^\$?\s*\\?rightarrow\s*\$?$/i.test(rawValue)
  );
};

const getStatusIcon = (value, toneClass) => {
  const icon = typeof value === "object" && value !== null ? value.icon : "";
  const normalizedIcon = normalizeText(icon);

  if (
    isRightArrow(value) ||
    ["arrow", "arrow-right", "rightarrow"].includes(normalizedIcon)
  ) {
    return faArrowRight;
  }
  if (normalizedIcon === "warning") return faTriangleExclamation;
  if (normalizedIcon === "more") return faEllipsis;
  if (toneClass === "success") return faCheck;
  if (toneClass === "warning") return faTriangleExclamation;
  return null;
};

const readValue = (row, key) => {
  if (!key) return "";
  return key.split(".").reduce((value, pathPart) => value?.[pathPart], row);
};

const formatValue = (value, columnType) => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "object") return value.label ?? value.value ?? "-";
  if (columnType === "currency" && typeof value === "number") {
    return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(value);
  }
  if (columnType === "number" && typeof value === "number") {
    return new Intl.NumberFormat("fr-FR").format(value);
  }
  return value;
};

const isStatusColumn = (column) => {
  const normalizedColumn = normalizeText(`${column.key} ${column.label}`);
  return column.type === "status" || ["status", "statut", "criticite", "priorite", "severity"].some((keyword) =>
    normalizedColumn.includes(keyword)
  );
};

const isGeneratedMarkdownId = (value) => /^markdown-row-\d+$/i.test(String(value ?? "").trim());

const shouldHideColumn = (column, rows) => {
  const normalizedColumn = normalizeText(`${column.key} ${column.label}`);
  if (!["id", "identifiant"].includes(normalizedColumn)) return false;

  const values = rows
    .filter((row) => row && row.type !== "group" && !row.group)
    .map((row) => readValue(row, column.key))
    .filter((value) => value !== null && value !== undefined && value !== "");

  return values.length > 0 && values.every(isGeneratedMarkdownId);
};

const buildColumns = (columns, rows) => {
  if (Array.isArray(columns) && columns.length > 0) {
    return columns
      .map((column) =>
        typeof column === "string"
          ? { key: column, label: column }
          : { ...column, key: column.key || column.accessor || column.label, label: column.label || column.Header || column.key }
      )
      .filter((column) => !shouldHideColumn(column, rows));
  }

  const firstDataRow = rows.find((row) => row && row.type !== "group" && !row.group);
  if (!firstDataRow) return [];

  return Object.keys(firstDataRow)
    .filter((key) => !["id", "__eliaRowKey", "type", "group"].includes(key))
    .map((key) => ({ key, label: key }));
};

const sortRows = (rows, sortConfig) => {
  if (!sortConfig.key) return rows;

  const groups = [];
  let activeGroup = null;

  rows.forEach((row) => {
    if (row?.type === "group" || row?.group) {
      activeGroup = { group: row, rows: [] };
      groups.push(activeGroup);
      return;
    }

    if (!activeGroup) {
      activeGroup = { group: null, rows: [] };
      groups.push(activeGroup);
    }
    activeGroup.rows.push(row);
  });

  const direction = sortConfig.direction === "asc" ? 1 : -1;
  return groups.flatMap(({ group, rows: groupRows }) => {
    const sortedGroupRows = [...groupRows].sort((a, b) => {
      const aValue = formatValue(readValue(a, sortConfig.key), sortConfig.type);
      const bValue = formatValue(readValue(b, sortConfig.key), sortConfig.type);
      return String(aValue).localeCompare(String(bValue), "fr", { numeric: true }) * direction;
    });

    return group ? [group, ...sortedGroupRows] : sortedGroupRows;
  });
};

const getStatusColumn = (columns) => columns.find(isStatusColumn);

const buildGeneratedStats = (rows, statusColumn) => {
  const toneCounts = rows.reduce((counts, row) => {
    if (row?.type === "group" || row?.group) return counts;
    const tone = statusColumn ? getToneClass(readValue(row, statusColumn.key)) : "neutral";
    return { ...counts, [tone]: (counts[tone] || 0) + 1 };
  }, {});

  const dataRows = rows.filter((row) => row && row.type !== "group" && !row.group);

  return [
    {
      label: "Elements",
      value: dataRows.length,
      helper: dataRows.length > 1 ? "lignes analysees" : "ligne analysee",
      tone: "neutral",
    },
    {
      label: "Haute priorite",
      value: toneCounts.danger || 0,
      helper: "a traiter en premier",
      tone: "danger",
    },
    {
      label: "A verifier",
      value: toneCounts.warning || 0,
      helper: "impact a confirmer",
      tone: "warning",
    },
  ];
};

const buildChartItems = (rows, statusColumn) => {
  if (!statusColumn) return [];

  const grouped = rows.reduce((items, row) => {
    if (row?.type === "group" || row?.group) return items;

    const rawValue = readValue(row, statusColumn.key);
    const label = formatValue(rawValue, statusColumn.type);
    const key = normalizeText(label) || "non renseigne";
    const existingItem = items[key] || { label, value: 0, tone: getToneClass(rawValue) };

    return {
      ...items,
      [key]: {
        ...existingItem,
        value: existingItem.value + 1,
      },
    };
  }, {});

  return Object.values(grouped).sort((a, b) => b.value - a.value);
};

export default function EliaTableGraph({ data }) {
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc", type: "" });
  const rows = Array.isArray(data?.rows) ? data.rows : [];
  const columns = useMemo(() => buildColumns(data?.columns, rows), [data?.columns, rows]);
  const sortedRows = useMemo(() => sortRows(rows, sortConfig), [rows, sortConfig]);
  const statusColumn = useMemo(() => getStatusColumn(columns), [columns]);
  const stats = useMemo(
    () => Array.isArray(data?.stats) && data.stats.length > 0 ? data.stats : buildGeneratedStats(rows, statusColumn),
    [data?.stats, rows, statusColumn]
  );
  const chartItems = useMemo(() => buildChartItems(rows, statusColumn), [rows, statusColumn]);
  const maxChartValue = Math.max(...chartItems.map((item) => item.value), 1);

  const handleSort = (column) => {
    if (column.sortable === false) return;
    setSortConfig((prev) => ({
      key: column.key,
      type: column.type,
      direction: prev.key === column.key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  if (!rows.length && !stats.length) return null;

  return (
    <div className="elia-table-graph">
      {data?.title && <div className="elia-table-graph-title">{data.title}</div>}

      {(stats.length > 0 || chartItems.length > 0) && (
        <div className={`elia-table-graph-overview ${chartItems.length === 0 ? "no-chart" : ""}`}>
          {stats.length > 0 && (
            <div className="elia-table-graph-stats">
              {stats.map((stat, index) => {
                const toneClass = getToneClass(stat);
                const icon = getStatusIcon(stat, toneClass);

                return (
                  <div key={`${stat.label || "stat"}-${index}`} className={`elia-table-graph-stat ${toneClass}`}>
                    <div className="elia-table-graph-stat-label">
                      {icon && <FontAwesomeIcon icon={icon} />}
                      <span>{stat.label}</span>
                    </div>
                    <div className="elia-table-graph-stat-value">{formatValue(stat.value, stat.type)}</div>
                    {stat.helper && <div className="elia-table-graph-stat-helper">{stat.helper}</div>}
                  </div>
                );
              })}
            </div>
          )}

          {chartItems.length > 0 && (
            <div className="elia-table-graph-chart">
              <div className="elia-table-graph-chart-title">Repartition {statusColumn?.label?.toLowerCase()}</div>
              <div className="elia-table-graph-chart-bars">
                {chartItems.map((item) => (
                  <div key={item.label} className="elia-table-graph-chart-row">
                    <div className="elia-table-graph-chart-meta">
                      <span>{item.label}</span>
                      <strong>{item.value}</strong>
                    </div>
                    <div className="elia-table-graph-chart-track">
                      <span
                        className={`elia-table-graph-chart-fill ${item.tone}`}
                        style={{ width: `${Math.max((item.value / maxChartValue) * 100, 8)}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {columns.length > 0 && rows.length > 0 && (
        <div className="elia-table-graph-table-wrap">
          <table className="elia-table-graph-table">
            <thead>
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.key}
                    className={column.align === "right" ? "is-right" : ""}
                    onClick={() => handleSort(column)}
                    title={column.sortable === false ? column.label : `Trier par ${column.label}`}
                  >
                    <span>{column.label}</span>
                    {column.sortable !== false && <FontAwesomeIcon icon={faSort} />}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((row, rowIndex) => {
                if (row?.type === "group" || row?.group) {
                  return (
                    <tr key={`group-${row.label || row.group}-${rowIndex}`} className="elia-table-graph-group">
                      <td colSpan={columns.length}>{row.label || row.group}</td>
                    </tr>
                  );
                }

                return (
                  <tr key={row.__eliaRowKey || row.id || `row-${rowIndex}`}>
                    {columns.map((column) => {
                      const value = readValue(row, column.key);
                      const isStatus = isStatusColumn(column);
                      const toneClass = isStatus ? getToneClass(value) : "";
                      const icon = isStatus ? getStatusIcon(value, toneClass) : null;

                      return (
                        <td key={`${row.__eliaRowKey || row.id || rowIndex}-${column.key}`} className={column.align === "right" ? "is-right" : ""}>
                          {isStatus ? (
                            <span className={`elia-table-graph-badge ${toneClass}`}>
                              {icon && <FontAwesomeIcon icon={icon} />}
                              {!isRightArrow(value) && (
                                <span>{formatValue(value, column.type)}</span>
                              )}
                            </span>
                          ) : (
                            <span>{formatValue(value, column.type)}</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
